/**
 * A voice note in a bubble.
 *
 * Voice notes were rendering as an empty bubble with nothing but a
 * timestamp: the bubble only ever drew message.body, and a voice note
 * has none. Reported as "is this how voice note shows on WhatsApp?",
 * which it very much was not.
 *
 * Built to the shape WhatsApp uses: a play button, a bar waveform that
 * fills as it plays and can be scrubbed, the duration, and a playback
 * speed toggle that appears once you have started it.
 *
 * The bars are drawn from the audio itself, not faked. Decoding happens
 * once per note, lazily, and the component works without it — a flat
 * row of bars is a fine resting state while it decodes, and the right
 * answer if decoding fails.
 */

import { useEffect, useRef, useState } from 'react'
import './VoiceNote.css'

const BARS = 34
const SPEEDS = [1, 1.5, 2]

/**
 * Average the samples into BARS buckets.
 *
 * Peak would be truer to the sound but reads as a wall of full-height
 * bars on anything compressed, which is what a voice note always is.
 */
function barsFrom (buffer) {
  const data = buffer.getChannelData(0)
  const size = Math.floor(data.length / BARS)
  const out = []

  for (let i = 0; i < BARS; i++) {
    let sum = 0
    for (let j = 0; j < size; j++) sum += Math.abs(data[i * size + j])
    out.push(sum / size)
  }

  // Normalised against the loudest bucket, so a quiet recording still
  // fills the row rather than drawing a flat line.
  const loudest = Math.max(...out, 0.0001)
  return out.map(v => Math.max(0.12, v / loudest))
}

export default function VoiceNote ({ url, mine, durationMs }) {
  const audioRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [current, setCurrent] = useState(0)
  /*
   * Seeded from the sender's own measurement.
   *
   * MediaRecorder writes WebM with no duration in it, so <audio> reports
   * Infinity until the file has been seeked to the end — which is why
   * the player sat at 0:00. The recorder now sends the length it timed,
   * and that is trusted until the element reports something finite.
   */
  const [duration, setDuration] = useState(
    Number.isFinite(durationMs) ? durationMs / 1000 : 0
  )
  const [speed, setSpeed] = useState(1)
  const [bars, setBars] = useState(null)
  const [started, setStarted] = useState(false)

  // Decoded once, in the background. A failure leaves bars null and the
  // flat fallback below is used.
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const response = await fetch(url)
        const bytes = await response.arrayBuffer()
        const ctx = new (window.AudioContext || window.webkitAudioContext)()
        const buffer = await ctx.decodeAudioData(bytes)
        if (alive) setBars(barsFrom(buffer))
        // Closed straight away: a context per voice note in a long
        // conversation hits the browser's limit and then none of them
        // can decode.
        ctx.close()
      } catch {
        if (alive) setBars(null)
      }
    })()
    return () => { alive = false }
  }, [url])

  function toggle () {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
    } else {
      audio.playbackRate = speed
      audio.play()
      setStarted(true)
    }
  }

  function cycleSpeed () {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length]
    setSpeed(next)
    if (audioRef.current) audioRef.current.playbackRate = next
  }

  /** Scrub by clicking anywhere on the waveform. */
  function seek (event) {
    const audio = audioRef.current
    if (!audio || !duration) return
    const box = event.currentTarget.getBoundingClientRect()
    const ratio = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1)
    audio.currentTime = ratio * duration
    setCurrent(audio.currentTime)
  }

  const progress = duration ? current / duration : 0
  const shape = bars ?? Array.from({ length: BARS }, () => 0.35)

  // Counts down while playing and shows the full length at rest, which
  // is what tells you how long it is before you commit to listening.
  const shown = playing || current > 0 ? current : duration
  const stamp = `${Math.floor(shown / 60)}:${String(Math.floor(shown % 60)).padStart(2, '0')}`

  return (
    <span className={`vn${mine ? ' is-mine' : ''}`}>
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={e => {
          // A stream recorded by MediaRecorder often reports Infinity
          // until it has been seeked; fall back to 0 so the row does not
          // print "Infinity:NaN".
          const d = e.currentTarget.duration
          // Only when the element knows better than the sender did.
          if (Number.isFinite(d) && d > 0) setDuration(d)
        }}
        onDurationChange={e => {
          const d = e.currentTarget.duration
          if (Number.isFinite(d) && d > 0) setDuration(d)
        }}
        onTimeUpdate={e => setCurrent(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCurrent(0) }}
      />

      <button
        type="button"
        className="vn__play"
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Play voice note'}
        data-testid="voice-play"
      >
        {playing ? (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <rect x="7" y="5" width="3.6" height="14" rx="1.2" />
            <rect x="13.4" y="5" width="3.6" height="14" rx="1.2" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5.2v13.6L19 12 8 5.2Z" />
          </svg>
        )}
      </button>

      <span
        className="vn__wave"
        onClick={seek}
        role="presentation"
        data-testid="voice-wave"
      >
        {shape.map((height, i) => (
          <span
            key={i}
            className={`vn__bar${i / BARS <= progress ? ' is-played' : ''}`}
            style={{ height: `${Math.round(height * 100)}%` }}
          />
        ))}
      </span>

      <span className="vn__meta">
        <span className="vn__time" data-testid="voice-time">{stamp}</span>
        {/* Only once it has been played, as WhatsApp does — a speed
            control on something you have not heard is noise. */}
        {started && (
          <button
            type="button"
            className="vn__speed"
            onClick={cycleSpeed}
            aria-label={`Playback speed ${speed}x`}
          >
            {speed}×
          </button>
        )}
      </span>
    </span>
  )
}
